import { toFix, toPlainDecimalString } from '../../../helpers/commonHelper';
import { resolveChainIdFromCoin } from '../../../constants/evmChain.registry';
import { filterBalanceWalletForTradeWallet } from '../../../services/wallet/linkedWallet.utils';

export function getAvailableBalanceForAsset(wallet, assetSymbol) {
  if (!Array.isArray(wallet) || !assetSymbol) return 0;
  const key = String(assetSymbol).toLowerCase();
  const row = wallet.find(
    (w) => String(w?.asset_name ?? '').toLowerCase() === key,
  );
  if (!row) return 0;
  const n = Number(row.available_balance);
  return Number.isFinite(n) ? n : 0;
}

const stripTestPrefix = (value) => {
  const key = String(value ?? '').trim().toLowerCase();
  if (key.length > 1 && key.startsWith('t') && !key.includes(':')) {
    return key.slice(1);
  }
  return key;
};

/**
 * Normalize decimal typing: digits only + fraction length.
 */
export function clampSafeTradeDecimalInput(value, maxFractionDigits = 4) {
  const raw = String(value ?? '');
  if (!raw) return '';

  const [intRaw = '', fracRaw = ''] = raw.split('.');
  const hasDot = raw.includes('.');
  const intPart = intRaw.replace(/\D/g, '') || (hasDot ? '0' : '');

  if (!hasDot) return intPart;

  const frac = fracRaw.replace(/\D/g, '').slice(0, Math.max(0, maxFractionDigits));
  return `${intPart || '0'}.${frac}`;
}

/** Keep amounts readable (avoid Number → 1e+24 / 4.8e-7 in UI copy). */
export function formatPlainDecimal(value) {
  return toPlainDecimalString(value);
}

function multiplyIntegerStrings(a, b) {
  if (a === '0' || b === '0') return '0';

  const result = Array(a.length + b.length).fill(0);

  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      const mul = Number(a[i]) * Number(b[j]);
      const pos1 = i + j;
      const pos2 = i + j + 1;
      const sum = mul + result[pos2];

      result[pos2] = sum % 10;
      result[pos1] += Math.floor(sum / 10);
    }
  }

  let start = 0;
  while (start < result.length - 1 && result[start] === 0) {
    start += 1;
  }

  return result.slice(start).join('');
}

/** Multiply two decimal strings without Number precision loss (for UI conversion). */
export function multiplyPlainDecimals(a, b, maxDecimals = 12) {
  const sa = String(a ?? '').trim();
  const sb = String(b ?? '').trim();
  if (!sa || !sb) return '0';

  const negA = sa.startsWith('-');
  const negB = sb.startsWith('-');
  const absA = negA ? sa.slice(1) : sa;
  const absB = negB ? sb.slice(1) : sb;

  if (!/^(\d+(\.\d*)?|\.\d+)$/.test(absA) || !/^(\d+(\.\d*)?|\.\d+)$/.test(absB)) {
    return '0';
  }

  const [intA = '0', fracA = ''] = absA.split('.');
  const [intB = '0', fracB = ''] = absB.split('.');
  const digitsA = `${intA}${fracA}`;
  const digitsB = `${intB}${fracB}`;
  const scale = fracA.length + fracB.length;

  if (!/^\d+$/.test(digitsA) || !/^\d+$/.test(digitsB)) return '0';

  const product = multiplyIntegerStrings(digitsA, digitsB);
  let result = product.toString();

  if (scale > 0) {
    if (result.length <= scale) {
      result = `0.${result.padStart(scale, '0')}`;
    } else {
      const intPart = result.slice(0, result.length - scale) || '0';
      let fracPart = result.slice(result.length - scale);
      if (maxDecimals != null && Number.isFinite(maxDecimals)) {
        fracPart = fracPart.slice(0, Math.max(0, maxDecimals));
      }
      fracPart = fracPart.replace(/0+$/, '');
      result = fracPart ? `${intPart}.${fracPart}` : intPart;
    }
  }

  result = result.replace(/^(-?)0+(?=\d)/, '$1');
  const negative = negA !== negB;
  if (negative && result !== '0') result = `-${result}`;
  return result;
}

export function isPositiveDecimalString(value) {
  const s = String(value ?? '').trim();
  if (!s || !/^(\d+(\.\d*)?|\.\d+)$/.test(s)) return false;
  return !/^0+(\.0+)?$/.test(s);
}

/** Pair limit of 0 / null / invalid means “no limit” (or unset). */
export function positivePairLimit(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * Client-side pair order limits from pair_list:
 * - minimum_trade → min quantity
 * - single_order_limit → max quantity (when > 0)
 * - maximum_order_limit → max order notional value qty×price (when > 0)
 */
export function getOrderLimitViolation({
  quantity,
  price,
  pair,
  referencePrice,
}) {
  if (!pair) return '';

  const qtyRaw = String(quantity ?? '').trim();
  if (!qtyRaw || !isPositiveDecimalString(qtyRaw)) return '';

  const qty = Number(qtyRaw);
  if (!Number.isFinite(qty) || qty <= 0) return '';

  const minTrade = positivePairLimit(pair.minimum_trade);
  if (minTrade != null && qty < minTrade) {
    return `Quantity must be at least ${toPlainDecimalString(minTrade)} (minimum trade).`;
  }

  const singleLimit = positivePairLimit(pair.single_order_limit);
  if (singleLimit != null && qty > singleLimit) {
    return `Quantity cannot exceed ${toPlainDecimalString(singleLimit)} (single order limit).`;
  }

  const maxOrderValue = positivePairLimit(pair.maximum_order_limit);
  if (maxOrderValue != null) {
    const pxRaw = isPositiveDecimalString(price)
      ? String(price).trim()
      : isPositiveDecimalString(referencePrice)
        ? String(referencePrice).trim()
        : '';
    if (pxRaw) {
      const notional = Number(multiplyPlainDecimals(qtyRaw, pxRaw, 12));
      if (Number.isFinite(notional) && notional > maxOrderValue) {
        return `Order value cannot exceed ${toPlainDecimalString(maxOrderValue)} (maximum order limit).`;
      }
    }
  }

  return '';
}

const getRowAssetName = (row) =>
  String(row?.asset_name ?? row?.assetName ?? '')
    .trim()
    .toLowerCase();

/**
 * Find the selected wallet's native gas asset row for an estimated_gas_fee feeCoin
 * (e.g. baseeth → eth / tbaseeth).
 */
export function findNativeGasBalanceRow(walletRows, feeCoin, selectedTradeWallet) {
  const fee = String(feeCoin ?? '').trim().toLowerCase();
  if (!fee || !Array.isArray(walletRows) || !walletRows.length) return null;

  const scoped = selectedTradeWallet
    ? filterBalanceWalletForTradeWallet(walletRows, selectedTradeWallet)
    : walletRows;

  const nativeRows = scoped.filter((row) => {
    const name = getRowAssetName(row);
    return name && !name.includes(':');
  });
  if (!nativeRows.length) return null;

  const feeStripped = stripTestPrefix(fee);
  const exact = nativeRows.find((row) => {
    const name = getRowAssetName(row);
    return name === fee || stripTestPrefix(name) === feeStripped;
  });
  if (exact) return exact;

  const feeChainId = resolveChainIdFromCoin(fee);
  if (feeChainId == null) return null;

  return (
    nativeRows.find((row) => resolveChainIdFromCoin(getRowAssetName(row)) === feeChainId) ||
    null
  );
}

export function getAvailableNativeGasBalance(walletRows, feeCoin, selectedTradeWallet) {
  const row = findNativeGasBalanceRow(walletRows, feeCoin, selectedTradeWallet);
  if (!row) return { available: 0, assetName: String(feeCoin || '').toLowerCase() };
  const n = Number(row.available_balance ?? row.availableBalance);
  return {
    available: Number.isFinite(n) ? n : 0,
    assetName: getRowAssetName(row) || String(feeCoin || '').toLowerCase(),
  };
}

export function getReferencePrice({
  selectedSide,
  sellOrderBook,
  buyOrderBook,
  limitPrice,
}) {
  const limit = Number(limitPrice);
  if (limit > 0) return limit;

  if (selectedSide === 0) {
    if (sellOrderBook?.length) {
      return Number(sellOrderBook[sellOrderBook.length - 1].price) || 0;
    }
    return 0;
  }

  if (buyOrderBook?.length) {
    return Number(buyOrderBook[0].price) || 0;
  }
  return 0;
}

export function computeQuantityFromPercent({
  percent,
  selectedSide,
  wallet,
  selectedPair,
  price,
  quantityDecimals,
  leverage = 1,
  isPerpetual = false,
}) {
  const pct = Number(percent);
  if (!selectedPair || !Number.isFinite(pct) || pct < 0) return '';

  const refPrice = Number(price);
  if (!(refPrice > 0)) return '';

  const decimal = quantityDecimals;

  if (selectedSide === 0) {
    const available = getAvailableBalanceForAsset(wallet, selectedPair.baseCurrency);
    const budget = available * (pct / 100);
    const effectiveBudget = isPerpetual ? budget * leverage : budget;
    const qty = effectiveBudget / refPrice;
    if (!(qty > 0)) return '';
    return String(toFix(qty, decimal));
  }

  const available = getAvailableBalanceForAsset(wallet, selectedPair.otherCurrency);
  const qty = available * (pct / 100);
  if (!(qty > 0)) return '';
  return String(toFix(qty, decimal));
}

export function computePercentFromQuantity({
  quantity,
  selectedSide,
  wallet,
  selectedPair,
  price,
  leverage = 1,
  isPerpetual = false,
}) {
  const qty = Number(quantity);
  const refPrice = Number(price);
  if (!selectedPair || !(qty > 0) || !(refPrice > 0)) return 0;

  if (selectedSide === 0) {
    const available = getAvailableBalanceForAsset(wallet, selectedPair.baseCurrency);
    if (!(available > 0)) return 0;
    const tradeAmount = qty * refPrice;
    const buyingPower = isPerpetual
      ? available * Math.max(Number(leverage) || 1, 1)
      : available;
    const pct = (tradeAmount / buyingPower) * 100;
    return Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) : 0;
  }

  const available = getAvailableBalanceForAsset(wallet, selectedPair.otherCurrency);
  if (!(available > 0)) return 0;
  const pct = (qty / available) * 100;
  return Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) : 0;
}

export function computePerpetualSummary({
  selectedSide,
  quantity,
  price,
  leverage,
  wallet,
  selectedPair,
  tradeFeePercent = 0,
  priceDecimals,
  quantityDecimals,
}) {
  const qty = Number(quantity);
  const refPrice = Number(price);
  const lev = Math.max(Number(leverage) || 1, 1);

  if (!(qty > 0) || !(refPrice > 0) || !selectedPair) {
    return {
      orderValue: 0,
      costMargin: 0,
      maxPosition: 0,
      estimatedLiqPrice: null,
      fees: 0,
    };
  }

  const orderValue = qty * refPrice;
  const costMargin = orderValue / lev;
  const availableMargin = getAvailableBalanceForAsset(wallet, selectedPair.baseCurrency);
  const maxPosition = refPrice > 0 ? (availableMargin * lev) / refPrice : 0;
  const feeRate = Number(tradeFeePercent) / 100;
  const fees = orderValue * (Number.isFinite(feeRate) ? feeRate : 0);

  let estimatedLiqPrice = null;
  if (lev > 1 && qty > 0) {
    const maintenanceMarginRate = 0.005;
    if (selectedSide === 0) {
      estimatedLiqPrice = refPrice * (1 - 1 / lev + maintenanceMarginRate);
    } else {
      estimatedLiqPrice = refPrice * (1 + 1 / lev - maintenanceMarginRate);
    }
  }

  const valueDec = Math.min((priceDecimals || 4) + (quantityDecimals || 4), 12);

  return {
    orderValue: Number(toFix(orderValue, valueDec)),
    costMargin: Number(toFix(costMargin, priceDecimals || 4)),
    maxPosition: Number(toFix(maxPosition, quantityDecimals || 4)),
    estimatedLiqPrice:
      estimatedLiqPrice != null && estimatedLiqPrice > 0
        ? Number(toFix(estimatedLiqPrice, priceDecimals || 4))
        : null,
    fees: Number(toFix(fees, priceDecimals || 4)),
  };
}
